export type OnboardingPersona = 'OWN-ENT' | 'OWN-IND' | 'DRV-SELF' | 'DRV-CO';

/** Owner product selection (server-validated against the catalog). */
export interface OnboardingSelection {
  feature: 'hours' | 'gps' | 'combo';
  billing: 'monthly' | 'annual';
  price_id: string;
  quantity: number;
}

/**
 * Own-account confirmed-company UX draft (server-resolved fiscal identity +
 * name). It is continuity only: it grants NO payer, coverage, employment,
 * invitation or company-administration authority (D-007/D-008/D-013/D-031/
 * D-050/D-053/D-055). The client never authors or edits these values.
 */
export interface DriverCompanySelection {
  company_nipc: string;
  company_name: string;
}

export interface DriverCompanyDraftSelection {
  driver_company: DriverCompanySelection;
}

export type OnboardingSelectionPayload = OnboardingSelection | DriverCompanyDraftSelection;

export interface OnboardingState {
  version: string;
  persona: OnboardingPersona;
  current_step: string;
  completed: boolean;
  selection?: OnboardingSelectionPayload | null;
}

export type OnboardingResumeRoute =
  | '/register/driver'
  | '/onboarding'
  | '/onboarding/state-error'
  | '/onboarding/company-contact-pending'
  | '/onboarding/employment'
  | '/onboarding/vehicle'
  | '/onboarding/owner'
  | '/dashboard/self-pay'
  | '/dashboard/paywall-expired'
  | '/dashboard/home';

const DRIVER_IDENTITY_STEPS = new Set(['nif']);
const DRIVER_COMPANY_STEPS = new Set(['company_confirm', 'payer']);
const DRIVER_SELF_PAY_STEPS = new Set([
  'product',
  'tax_sync',
  'pending_until_webhook',
  'canceled',
  'self_pay_active',
  'company_contact_submitted',
]);
const DRIVER_PENDING_OUTCOMES = new Set([
  'pending_no_license',
  'pending_no_invitation',
  'company_contact_submitted',
]);

export const isPendingCompanyPayState = (state: OnboardingState): boolean => (
  state.persona === 'DRV-CO'
  && state.completed === false
  && (
    state.current_step === 'pending_no_license'
    || state.current_step === 'pending_no_invitation'
  )
);
const OWNER_STEPS = new Set([
  'company_name',
  'company_nipc',
  'license',
  'also_drives',
  'fleet_size',
  'product_phase1',
  'config_phase2',
  'registration_only',
  'tax_sync',
  'trial_active_no_card',
  'd0_add_card',
  'done',
]);
const STATE_ERROR_ROUTE: OnboardingResumeRoute = '/onboarding/state-error';
export const ONBOARDING_STATE_VERSION = '1.8.2';

const isOnboardingPersona = (value: string): value is OnboardingPersona => (
  value === 'OWN-ENT'
  || value === 'OWN-IND'
  || value === 'DRV-SELF'
  || value === 'DRV-CO'
);

const isKnownStepForPersona = (state: OnboardingState): boolean => {
  if (state.persona === 'OWN-ENT' || state.persona === 'OWN-IND') {
    return OWNER_STEPS.has(state.current_step);
  }
  if (state.persona === 'DRV-SELF') {
    return new Set([
      ...DRIVER_IDENTITY_STEPS,
      ...DRIVER_COMPANY_STEPS,
      ...DRIVER_SELF_PAY_STEPS,
      'done',
    ]).has(state.current_step);
  }
  if (state.persona === 'DRV-CO') {
    return new Set([
      ...DRIVER_IDENTITY_STEPS,
      ...DRIVER_COMPANY_STEPS,
      ...DRIVER_PENDING_OUTCOMES,
      'active_by_invitation',
      'done',
    ]).has(state.current_step);
  }
  return false;
};

export const isOnboardingSelection = (value: unknown): value is OnboardingSelection => {
  if (!value || typeof value !== 'object') return false;
  const selection = value as Record<string, unknown>;
  return (
    (selection.feature === 'hours' || selection.feature === 'gps' || selection.feature === 'combo')
    && (selection.billing === 'monthly' || selection.billing === 'annual')
    && typeof selection.price_id === 'string'
    && selection.price_id.length > 0
    && typeof selection.quantity === 'number'
    && Number.isInteger(selection.quantity)
    && selection.quantity >= 1
  );
};

const DRIVER_DRAFT_PERSONAS = new Set<OnboardingPersona>(['DRV-SELF', 'DRV-CO']);

/**
 * Only these incomplete, pre-association driver steps may carry/restore a
 * confirmed-company draft. `payer`/terminal steps are deliberately excluded so
 * the draft can never imply a payer or coverage decision.
 */
export const DRIVER_COMPANY_DRAFT_STEPS = new Set<string>(['nif', 'company_confirm']);

/**
 * Shape guard for the server-authored driver_company selection. `isAuthoritativeOnboardingState` uses it as an
 * alternative to the owner product selection WITHOUT relaxing the owner
 * validation: the driver payload can only ever be accepted for a driver persona
 * and can never satisfy the owner `config_phase2` selection requirement.
 */
export const isDriverCompanySelection = (value: unknown): value is DriverCompanyDraftSelection => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const selection = value as Record<string, unknown>;
  const selectionKeys = Object.keys(selection);
  if (selectionKeys.length !== 1 || selectionKeys[0] !== 'driver_company') return false;
  const company = selection.driver_company;
  if (!company || typeof company !== 'object' || Array.isArray(company)) return false;
  const draft = company as Record<string, unknown>;
  const draftKeys = Object.keys(draft).sort();
  if (draftKeys.length !== 2 || draftKeys[0] !== 'company_name' || draftKeys[1] !== 'company_nipc') {
    return false;
  }
  return (
    typeof draft.company_nipc === 'string' && draft.company_nipc.trim().length > 0
    && typeof draft.company_name === 'string' && draft.company_name.trim().length > 0
  );
};

const normalizeSelection = (
  persona: OnboardingPersona,
  raw: unknown,
): OnboardingSelectionPayload | null => {
  if (isOnboardingSelection(raw)) return raw;
  if (DRIVER_DRAFT_PERSONAS.has(persona) && isDriverCompanySelection(raw)) return raw;
  return null;
};

/**
 * Return the restorable confirmed-company draft for an incomplete, own-account
 * driver still on a permitted pre-association step. Returns null for any other
 * persona/step/terminal state so a draft cannot leak into payer, coverage or a
 * cross-account context.
 */
export const driverCompanyDraftFromState = (
  state: OnboardingState,
): DriverCompanySelection | null => {
  if (!DRIVER_DRAFT_PERSONAS.has(state.persona) || state.completed) return null;
  if (!DRIVER_COMPANY_DRAFT_STEPS.has(state.current_step)) return null;
  if (!isDriverCompanySelection(state.selection)) return null;
  return state.selection.driver_company;
};


/**
 * Validate the semantic state machine at the trust boundary. A known step is
 * not enough: terminal outcomes belong to one persona and may only be marked
 * completed when their authoritative server fact exists.
 */
export const isSemanticallyValidOnboardingState = (state: OnboardingState): boolean => {
  if (state.version !== ONBOARDING_STATE_VERSION || !isKnownStepForPersona(state)) {
    return false;
  }

  if (state.current_step === 'active_by_invitation') {
    return state.persona === 'DRV-CO' && state.completed === true;
  }
  if (state.current_step === 'self_pay_active') {
    return state.persona === 'DRV-SELF' && state.completed === true;
  }
  if (state.current_step === 'done') {
    return state.completed === true;
  }
  if (state.current_step === 'config_phase2') {
    return (
      (state.persona === 'OWN-ENT' || state.persona === 'OWN-IND')
      && state.completed === false
      && isOnboardingSelection(state.selection)
    );
  }
  if (state.current_step === 'registration_only') {
    // #448: the owner explicitly declined the commercial selection. Only a
    // COMPLETED registration-only outcome is valid — a bare pointer must not be
    // replayed as a finished owner journey.
    return (
      (state.persona === 'OWN-ENT' || state.persona === 'OWN-IND')
      && state.completed === true
    );
  }
  if (state.current_step === 'trial_active_no_card' || state.current_step === 'd0_add_card') {
    return (
      (state.persona === 'OWN-ENT' || state.persona === 'OWN-IND')
      && state.completed === false
    );
  }

  // Every non-terminal resume pointer is incomplete. In particular, a client
  // must not turn a company outcome into a completed state by replaying a stale
  // payload from another device.
  return state.completed === false;
};

/** Maps only authenticated server state. No AsyncStorage value is accepted. */
export function routeForOnboardingState(state: OnboardingState): OnboardingResumeRoute {
  // A malformed or semantically unknown state must not enter the heuristic
  // onboarding index. The dedicated error route has no resume classification.
  if (!isSemanticallyValidOnboardingState(state)) return STATE_ERROR_ROUTE;

  if (state.persona === 'OWN-ENT' || state.persona === 'OWN-IND') {
    if (state.current_step === 'd0_add_card') return '/dashboard/paywall-expired';
    if (state.current_step === 'trial_active_no_card') return '/dashboard/home';
    // #448: an owner who declined the mandatory commercial selection keeps
    // authorized management access — the purchase onboarding must not replay.
    if (state.current_step === 'registration_only') return '/dashboard/home';
    if (state.completed && state.current_step === 'done') return '/dashboard/home';
    return '/onboarding/owner';
  }

  if (state.persona === 'DRV-CO' && state.current_step === 'active_by_invitation') {
    return '/dashboard/home';
  }
  if (state.persona === 'DRV-SELF' && state.current_step === 'self_pay_active') {
    return '/dashboard/home';
  }
  if (state.completed && state.current_step === 'done') return '/dashboard/home';
  if (DRIVER_IDENTITY_STEPS.has(state.current_step)) return '/register/driver';
  if (state.persona === 'DRV-SELF' && DRIVER_SELF_PAY_STEPS.has(state.current_step)) {
    return '/dashboard/self-pay';
  }
  // Company-confirmation / payer resume markers must land in the canonical
  // registration journey (`/register/driver`), which runs company confirmation
  // -> EXPLICIT payer -> association. The legacy `/onboarding/employment`
  // screen associates WITHOUT a payer (D-031/D-053), so it must never be the
  // resume target for these markers.
  if (DRIVER_COMPANY_STEPS.has(state.current_step)) return '/register/driver';
  if (state.persona === 'DRV-CO' && state.current_step === 'company_contact_submitted') {
    return '/onboarding/company-contact-pending';
  }
  if (state.persona === 'DRV-CO' && DRIVER_PENDING_OUTCOMES.has(state.current_step)) {
    return '/onboarding';
  }
  return STATE_ERROR_ROUTE;
}

export const isAuthoritativeOnboardingState = (value: unknown): value is OnboardingState => {
  if (!value || typeof value !== 'object') return false;
  const state = value as Record<string, unknown>;
  if (
    typeof state.version !== 'string'
    || typeof state.persona !== 'string'
    || !isOnboardingPersona(state.persona)
    || typeof state.current_step !== 'string'
    || typeof state.completed !== 'boolean'
  ) {
    return false;
  }
  return isSemanticallyValidOnboardingState({
    version: state.version,
    persona: state.persona,
    current_step: state.current_step,
    completed: state.completed,
    selection: normalizeSelection(state.persona as OnboardingPersona, state.selection),
  });
};
